const text = value => typeof value === 'string' && value.trim().length > 0;
const date = value => text(value) && /^\d{4}-\d{2}-\d{2}$/.test(value) && new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value;
const timestamp = value => text(value) && /T.*(?:Z|[+-]\d{2}:\d{2})$/.test(value) && Number.isFinite(Date.parse(value));
const strings = value => Array.isArray(value) && value.every(text);

export function isSourceUrl(value) {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password;
  } catch { return false; }
}

// Embedded raster only: opening a private brief must not contact an image host.
export function representativeImage(value) {
  return typeof value === 'string' && /^data:image\/(?:png|jpeg|webp);base64,[A-Za-z0-9+/]+={0,2}$/.test(value) ? value : null;
}

export function parseCandidateFile(raw) {
  let batch;
  try { batch = JSON.parse(raw); } catch { throw new Error('json'); }
  try {
    if (!batch || !date(batch.date) || (batch.generated_at != null && !timestamp(batch.generated_at)) || !Array.isArray(batch.candidates) || batch.candidates.length > 4) throw new Error();
    if (batch.country != null && !/^[A-Z]{2}$/.test(batch.country)) throw new Error();
    if (batch.city != null && !text(batch.city)) throw new Error();
    const archive = batch.source_type === 'archive';
    if (batch.source_type != null && !['survey', 'archive'].includes(batch.source_type)) throw new Error();
    const ids = new Set();
    const ranks = new Set();
    const counts = { external: 0, everyday: 0 };
    for (const candidate of batch.candidates) {
      if (!candidate || !text(candidate.id) || ids.has(candidate.id) || !Object.hasOwn(counts, candidate.kind)) throw new Error();
      if (!Number.isInteger(candidate.rank) || candidate.rank < 1 || candidate.rank > batch.candidates.length || ranks.has(candidate.rank)) throw new Error();
      if (!text(candidate.title)) throw new Error();
      if (!archive && !['hook', 'person_mind', 'reason', 'last_message', 'fancian_connection'].every(field => text(candidate[field]))) throw new Error();
      if (archive && (!candidate.creative_reference || !['creative_id', 'title', 'readme'].every(field => text(candidate.creative_reference[field])))) throw new Error();
      if (!strings(candidate.scenes) || (!archive && candidate.scenes.length !== 3) || !strings(candidate.facts) || !strings(candidate.hypotheses)) throw new Error();
      if (!Array.isArray(candidate.sources) || (!archive && candidate.kind === 'external' && !candidate.sources.some(source => isSourceUrl(source?.url)))) throw new Error();
      if (!candidate.sources.every(source => source && text(source.title) && (isSourceUrl(source.url) || (source.url == null && text(source.local_path))) && (date(source.checked_at) || timestamp(source.checked_at)))) throw new Error();
      if (candidate.valid_until != null && !date(candidate.valid_until)) throw new Error();
      ids.add(candidate.id);
      ranks.add(candidate.rank);
      counts[candidate.kind] += 1;

    }
  } catch { throw new Error('fields'); }
  return { ...batch, candidates: [...batch.candidates].sort((a, b) => a.rank - b.rank) };
}

export function candidateCopy(candidate, batch, translate) {
  const label = key => translate(`contentCandidates.${key}`);
  if (batch.source_type === 'archive') return [label('archive'), batch.date, candidate.title, ...creativeReferenceCopy(candidate.creative_reference, translate)].join('\n\n');
  return [
    `${batch.date} · ${batch.generated_at || label('timeUnknown')}`,
    `#${candidate.rank} ${candidate.title} (${label(candidate.kind)})`,
    ...['hook', 'person_mind', 'reason'].map(field => `${label(field)}: ${candidate[field]}`),
    `${label('scenes')}:\n${candidate.scenes.map((scene, index) => `${index + 1}. ${scene}`).join('\n')}`,
    ...['last_message', 'fancian_connection'].map(field => `${label(field)}: ${candidate[field]}`),
    ...['facts', 'hypotheses'].map(field => `${label(field)}:\n${candidate[field].join('\n') || label('none')}`),
    `${label('sources')}:\n${candidate.sources.map(source => `${source.title} — ${source.url || source.local_path} (${label('checked')}: ${source.checked_at})`).join('\n') || label('none')}`,
    `${label('validUntil')}: ${candidate.valid_until || label('notSpecified')}`,
    ...creativeReferenceCopy(candidate.creative_reference, translate),
    ...(candidate.post_suggestion ? [`${label('postSuggestion')}:\n${[candidate.post_suggestion.body, candidate.post_suggestion.tags?.map(tag => `#${tag.replace(/^#/, '')}`).join(' '), candidate.post_suggestion.place].filter(Boolean).join('\n')}`] : []),
  ].join('\n\n');
}

export function creativeReferenceCopy(reference, translate) {
  if (!reference) return [];
  const label = key => translate(`contentCandidates.${key}`);
  return [
    `${label('creativeReference')}: ${reference.creative_id} · ${reference.title}`,
    ...['readme', 'latest_review_video', 'representative_image'].filter(field => reference[field]).map(field => `${label(`referenceFields.${field}`)}: ${reference[field]}`),
    `${label('publicationReport')}: ${label(`publications.${reference.publication_status}`)}`,
    ...(reference.publication ? [`${label('reportedCaption')}: ${reference.publication.caption}`, `${label('reportedPlace')}: ${reference.publication.location_label}`, `${label('referenceFields.evidence')}: ${reference.publication.source_file}`] : []),
    ...(reference.context ? [reference.context] : []),
  ];
}
